import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { IReminderSettings, ReminderSettingsRequest } from "../library/models/reminderSettings";
import { BaseService } from "./baseService";

/**
 * When the clinic reminds its patients about tomorrow.
 *
 * Neither route names a clinic: the API takes it from the session, so this reads the caller's
 * own settings and writes to nobody else's. Both are the clinic admin's alone, and the API
 * refuses either way rather than trusting the tab to have been hidden.
 */
export class ReminderSettingsService extends BaseService {
    /**
     * Keyed by the clinic even though the request does not name one -- the same trap
     * `TenantBrandingService.getBranding` documents: without the tenant in the key, somebody
     * signing out of one clinic and into another in a single page load is served the first
     * clinic's settings from cache. Hand-written rather than built with createGetQuery, whose
     * key is the URL and its query parameters, because the tenant is neither.
     */
    getSettings(tenantId: number | null): UseQueryOptions<IReminderSettings, Error> {
        return {
            queryKey: ['/reminder-settings', tenantId],
            queryFn: (): Promise<IReminderSettings> =>
                this.get<IReminderSettings>('/reminder-settings'),
        };
    }

    /**
     * Sends all three settings: this is a full replace, not a patch, because "reminders are on
     * but nobody said when" is not a state the backend has an answer for.
     *
     * Hand-written rather than built with createMutation, which only speaks POST/PATCH/DELETE
     * shapes. Resolves with the stored result so the caller renders what was saved rather than
     * trusting its own draft.
     */
    saveSettingsMutation(): UseMutationOptions<IReminderSettings, Error, ReminderSettingsRequest> {
        return {
            mutationFn: (payload: ReminderSettingsRequest): Promise<IReminderSettings> =>
                this.put<IReminderSettings>('/reminder-settings', payload),
        };
    }
}
